import React from "react";
import { Award, GraduationCap, Clock, TrendingUp, Target, MessageSquare } from "lucide-react";
import { Label, ScoreRing, scoreColor, Avatar, Stars, RiskBadge } from "./common";
import { TrendArea, BarSeries } from "./charts";
import { fmtDateTime } from "../lib/time";

export default function ServiceCV({ cv, onOpenFeedback }) {
  const emp = cv.employee;
  const s = cv.score;
  const ce = cv.current_employer;
  const tpLabels = { welcomed: "Felt Valued", understood: "Needs Understanding", explained: "Communication", resolved: "Problem Resolution" };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center">
        <Avatar name={emp.name} src={emp.photo_url} size={80} />
        <div className="flex-1">
          <h1 className="font-head text-2xl font-bold text-slate-900">{emp.name}</h1>
          <p className="text-sm text-slate-500">{emp.job_title || "Service Professional"}</p>
          {ce && (ce.org_name || ce.location) && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="cv-current-employer">
              {ce.org_logo && <img src={ce.org_logo} alt={ce.org_name} className="h-6 w-6 rounded object-contain" />}
              {ce.org_name && <span className="text-sm font-semibold text-slate-700">{ce.org_name}</span>}
              {ce.location && <span className="text-sm text-slate-400">· {ce.location}</span>}
            </div>
          )}
          {cv.tenure && <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> {cv.tenure.label} tenure</p>}
          {cv.last_updated && (
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid="cv-last-updated">
              Last updated · {fmtDateTime(cv.last_updated)}
            </p>
          )}
        </div>
        <ScoreRing value={s.ssi} testId="cv-service-index" size={120} />
      </div>

      {!s.meets_threshold && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="cv-threshold-note">
          Based on {s.verified_interactions} verified interactions. A minimum of {s.min_reviews} is recommended before this score is treated as conclusive.
        </div>
      )}

      {/* Headline metrics */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Big label="Verified Interactions" value={s.verified_interactions.toLocaleString()} />
        <Big label="Customers Felt Valued" value={s.felt_valued_pct != null ? `${s.felt_valued_pct}%` : "—"} color="#10B981" />
        <Big label="Problem Resolution" value={s.resolution_pct != null ? `${s.resolution_pct}%` : "—"} color={s.resolution_pct != null ? scoreColor(s.resolution_pct / 20) : "#0F172A"} />
        <Big label="Communication" value={s.communication_pct != null ? `${s.communication_pct}%` : "—"} color="#4338CA" />
        <Big label="Commendations" value={cv.commendations} color="#F59E0B" icon={Award} />
        <Big label="Training Certifications" value={cv.training_certifications} color="#4338CA" icon={GraduationCap} />
        <Big label="Confidence" value={`${Math.round(s.confidence * 100)}%`} />
        <Big label="Raw Average" value={s.raw_average ? s.raw_average.toFixed(2) : "—"} />
      </div>

      {/* Benchmarks + ratings history */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="cv-benchmarks">
          <Label>How this compares</Label>
          <p className="mt-1 text-xs text-slate-400">Service Index vs company and industry.</p>
          <div className="mt-4 space-y-3">
            <Bench label="This employee" value={cv.benchmarks?.you} accent />
            <Bench label="Company average" value={cv.benchmarks?.company} />
            <Bench label="Industry average" value={cv.benchmarks?.industry} />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <Label>Ratings history</Label>
          <div className="mt-3"><BarSeries data={cv.ratings_history} yKey="count" xKey="label" testId="cv-history-chart" /></div>
        </div>
      </div>

      {/* All-time consolidated */}
      {cv.all_time && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="cv-all-time">
          <Label>All-time consolidated</Label>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <Big label="Total ratings" value={cv.all_time.total_ratings} />
            <Big label="Avg index" value={cv.all_time.avg_index} color="#4338CA" />
            <Big label="Avg rating" value={cv.all_time.avg_rating} />
            <Big label="Felt valued" value={cv.all_time.felt_valued_pct != null ? `${cv.all_time.felt_valued_pct}%` : "—"} color="#10B981" />
            <Big label="Resolution" value={cv.all_time.resolution_pct != null ? `${cv.all_time.resolution_pct}%` : "—"} />
            <Big label="Communication" value={cv.all_time.communication_pct != null ? `${cv.all_time.communication_pct}%` : "—"} color="#4338CA" />
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <Label>Service Trend · 30 days</Label>
          <div className="mt-3"><TrendArea data={cv.trend} testId="cv-trend-chart" /></div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <Label>Strengths</Label>
            <div className="mt-3 space-y-2">
              {cv.strengths.length === 0 && <p className="text-sm text-slate-400">—</p>}
              {cv.strengths.map((x) => (
                <div key={x.key} className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm text-slate-600"><TrendingUp className="h-4 w-4 text-emerald-500" />{x.label}</span><span className="sv-metric font-bold text-emerald-600">{x.value}%</span></div>
              ))}
            </div>
          </div>
          {cv.gap && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5" data-testid="cv-gap">
              <Label className="text-indigo-700">Primary Service Gap</Label>
              <p className="mt-1 flex items-center gap-2 font-head text-lg font-semibold text-indigo-900"><Target className="h-4 w-4" />{cv.gap.gap_label}</p>
              <p className="mt-2 text-sm text-indigo-700">Recommended: {cv.gap.recommended_training}</p>
              <p className="text-xs text-indigo-500">Est. {cv.gap.estimated_minutes} min</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent feedback */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <Label>Recent Verified Feedback</Label>
        <div className="mt-3 space-y-3">
          {cv.recent_feedback.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No feedback yet.</p>}
          {cv.recent_feedback.map((f) => (
            <div key={f.id} onClick={onOpenFeedback ? () => onOpenFeedback(f.id) : undefined}
              className={`rounded-lg border border-slate-100 p-3 ${onOpenFeedback ? "cursor-pointer transition-colors hover:border-indigo-300 hover:bg-indigo-50/20" : ""}`} data-testid={`cv-feedback-${f.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><Stars value={f.rating} /><RiskBadge status={f.review_status} /></div>
                <span className="text-xs text-slate-400">{fmtDateTime(f.created_at, f.location_id)}</span>
              </div>
              {f.comment && <p className="mt-2 flex gap-2 text-sm text-slate-600"><MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-300" />{f.comment}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Big({ label, value, color = "#0F172A", icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sv-rise">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {Icon && <Icon className="h-4 w-4 text-slate-300" />}
      </div>
      <p className="sv-metric mt-2 text-2xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

function Bench({ label, value, accent }) {
  const pct = Math.min(100, ((value || 0) / 5) * 100);
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm"><span className="text-slate-600">{label}</span><span className="font-semibold text-slate-900">{value == null ? "—" : value}</span></div>
      <div className="h-2.5 w-full rounded-full bg-slate-100"><div className={`h-2.5 rounded-full ${accent ? "bg-[#4338CA]" : "bg-slate-400"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
